import React, { FC, useState } from 'react';
import { Button, FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MillingFilters, MillingJobType, MillingMaterial } from 'models/milling';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { millingJobTypesTemplate } from 'apollo/templates/milling';
import { useQuery } from '@apollo/client';
import { vitaColors } from 'models/vita-colors-data';

type MillingJobsPageActionsProps = {
    onFilterChange: (filters: MillingFilters) => void;
    onToggleShowSets: (showSets: boolean) => void;
};

const MillingJobsPageActions: FC<MillingJobsPageActionsProps> = ({
    onFilterChange,
    onToggleShowSets,
}: MillingJobsPageActionsProps) => {
    const { t: translate } = useTranslation(['milling-jobs', 'construction-props']);
    const [showSets, setShowSets] = useState(false);
    const [millingJobTypes, setMillingJobTypes] = useState<MillingJobType[]>([]);
    const [jobType, setJobType] = useState('_ALL_');
    const [material, setMaterial] = useState('');
    const [color, setColor] = useState('');
    const [availableMaterials, setAvailableMaterials] = useState<string[]>([]);
    const [availableColors, setAvailableColors] = useState<string[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();

    useQuery(millingJobTypesTemplate, {
        onCompleted: (data) => {
            setMillingJobTypes(data.millingJobTypes);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleJobTypeChanged = (jobTypeName: string) => {
        setJobType(jobTypeName);
        setMaterial('');
        setColor('');
        setAvailableColors([]);
        setAvailableMaterials([]);
        onFilterChange({ jobType: jobTypeName !== '_ALL_' ? jobTypeName : undefined });

        const selectedJobType = millingJobTypes.find((jobType) => jobType.name === jobTypeName);
        if (selectedJobType) {
            setAvailableMaterials(selectedJobType.materials.map((material) => (material as MillingMaterial).name));
        }
    };

    const handleMaterialChanged = (materialName: string) => {
        const selectedJobType = millingJobTypes.find((findJobType) => findJobType.name === jobType);
        if (selectedJobType) {
            const selectedMaterial = (selectedJobType.materials as MillingMaterial[]).find(
                (material) => material.name === materialName,
            );
            setMaterial(materialName);
            setColor('');

            let colors = [] as string[];
            colors = selectedMaterial?.useVitaColors ? colors.concat(vitaColors) : colors;
            setAvailableColors(colors.concat(selectedMaterial?.additionalColors || []));
            onFilterChange({ jobType: jobType, material: materialName });
        }
    };

    const handleColorChanged = (colorName: string) => {
        setColor(colorName);
        onFilterChange({ jobType: jobType, material: material, color: colorName });
    };

    const toggleShowSets = () => {
        onToggleShowSets(!showSets);
        setShowSets(!showSets);
    };

    return (
        <Grid container rowSpacing={1.5} columnSpacing={1}>
            <Grid item md={2} sm={2} xs={12}>
                <FormControl fullWidth>
                    <InputLabel id="material-select-label">{translate('milling-jobs:TYPE_OF_JOB')}</InputLabel>
                    <Select
                        labelId="material-select-label"
                        label={translate('milling-jobs:TYPE_OF_JOB')}
                        value={jobType}
                        onChange={(e) => handleJobTypeChanged(e.target.value)}
                    >
                        <MenuItem value="_ALL_">Alla</MenuItem>
                        {millingJobTypes.map((jobType) => (
                            <MenuItem key={jobType._id} value={jobType.name}>
                                {jobType.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item sm={2} xs={12}>
                <FormControl fullWidth>
                    <InputLabel id="material-select-label">{translate('construction-props:MATERIAL')}</InputLabel>
                    <Select
                        labelId="material-select-label"
                        label={translate('construction-props:MATERIAL')}
                        value={material}
                        onChange={(e) => handleMaterialChanged(e.target.value)}
                        disabled={availableMaterials.length === 0}
                    >
                        {availableMaterials.map((material) => (
                            <MenuItem key={material} value={material}>
                                {material}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item sm={2} xs={12}>
                {availableColors.length > 0 ? (
                    <FormControl fullWidth>
                        <InputLabel id="color-select-label">{translate('construction-props:COLOR')}</InputLabel>
                        <Select
                            labelId="color-select-label"
                            label={translate('construction-props:COLOR')}
                            value={color}
                            onChange={(e) => handleColorChanged(e.target.value)}
                        >
                            {availableColors.map((color) => (
                                <MenuItem key={color} value={color}>
                                    {color}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : undefined}
            </Grid>
            <Grid item sm={4} xs={false}></Grid>
            <Grid item sm={2} xs={12}>
                <Button fullWidth variant="contained" onClick={() => toggleShowSets()}>
                    {!showSets ? (
                        <span>{translate('milling-jobs:SHOW_ONGOING_FINISHED')}</span>
                    ) : (
                        <span>{translate('milling-jobs:SHOW_TODO')}</span>
                    )}
                </Button>
            </Grid>
        </Grid>
    );
};

export default MillingJobsPageActions;
